import React from 'react';
import { Formik } from 'formik';
import { Button, Col, Form } from 'react-bootstrap';
import * as Yup from 'yup';
import Select from 'react-select';
import i18next from 'i18next';
import { ReactSelectOption } from '../../../../types/index';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../../hooks/useApiClient';
import toaster from '../../../../utils/toaster';
import useModalStore from '../../../../store/ModalStore';
import useLeadsStore from '../../../../store/LeadsStore';
import { LeadMotivationEnum, LeadStatusEnum } from '../../../../api/models/Enums';
import { translateLeadMotivation } from '../../../../utils/translations';

const LeadUnqualified = () => {
  const { t } = useTranslation();
  const { closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { activeLead } = useLeadsStore();
  const initialValues = {
    Motivazione__c: '' as LeadMotivationEnum,
  };

  const validationSchema = Yup.object().shape({
    Motivazione__c: Yup.string().required(i18next.t('mandatory_field')),
  });

  const { data: motivationOptions } = useQuery({
    queryKey: ['unqualified_motivations'],
    queryFn: async (): Promise<ReactSelectOption[]> => {
      const response = await apiClient.leadsControllerService.getLeadFields();
      const motivations = response
      .find(item => item.name === 'Motivazione__c')?.picklistValues.filter(item => item.active)
      .map(item => ({...item, label: translateLeadMotivation(item.label)}));

      return motivations
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });

  const unqualifyLead = useMutation({
    mutationFn: ({ leadId, motivation }: { leadId: string; motivation: LeadMotivationEnum }) =>
      apiClient.leadsControllerService.unqualifyLead(leadId, motivation, LeadStatusEnum.UNQUALIFIED),
    onSuccess: () => {
      toaster.success(t('lead_unqualified_succesfully'));
      queryClient.invalidateQueries({ queryKey: ['leads_list'] });
      closeModal();
    },
    onError: (error) => {
      console.error(i18next.t('error_unqualify_lead'), error);
      toaster.error(i18next.t('error_unqualify_lead'));
    },
  });

  const handleFormSubmit = (values: { Motivazione__c: LeadMotivationEnum }) => {
    if (!activeLead?.Id) {
      toaster.error(t('no_active_lead'));
      return;
    }
    unqualifyLead.mutate({ leadId: activeLead.Id, motivation: values.Motivazione__c });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <Form onSubmit={handleSubmit} id='unqualify-lead-form' className='d-flex flex-column  align-items-center'>
          <Col sm={6} style={{ width: "100%" }}>
            <Form.Group className="mb-3 mt-3">
              {/* <Form.Label>{t('motivation')}</Form.Label> */}
              <Select
                menuPosition="fixed"
                options={motivationOptions}
                onChange={(selected) => {
                  setFieldValue('Motivazione__c', selected ? selected.value : null);
                }}
                value={
                  motivationOptions?.find(
                    (option) => (values.Motivazione__c ?? '') === option.value
                  ) || null
                }
                className={`custom-select ${touched.Motivazione__c && errors.Motivazione__c ? 'select-error' : ''
                  }`}
              />
              {touched.Motivazione__c && errors.Motivazione__c ? (
                <div style={{ color: 'red' }}>{errors.Motivazione__c}</div>
              ) : null}
            </Form.Group>
          </Col>
          <Button className='submit-btn' type="submit">{t('submit')}</Button>
        </Form>
      )}
    </Formik>
  );
};

export default LeadUnqualified;
